import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

// Mapping between module keys in DB and navigation paths
const MODULE_TO_ROUTES = {
  // Mandatory
  incidents: ['/incidents', '/incidents/new'],
  users: ['/users'],
  assignment_groups: ['/assignment-groups'],
  reports: ['/reports'],
  survey_analytics: ['/survey-analytics', '/nps-dashboard', '/nps-surveys'],
  email_integration: ['/integrations'],
  // Optional
  service_requests: ['/service-requests-queue', '/my-requests', '/request-catalog'],
  changes: ['/changes', '/cab-console'],
  problems: ['/problems'],
  knowledge: ['/knowledge-management', '/help-center'],
  assets: ['/assets'],
  service_catalog: ['/service-catalog', '/task-flow-designer', '/workflow-templates'],
  task_management: ['/task-queue', '/queue'],
  chatbot: ['/support-chat'],
  call_center: ['/call-center', '/request-callback'],
  live_chat: ['/live-chat', '/amie-learning'],
  work_console: ['/work-console', '/supervisor-dashboard'],
  module_subscriptions: ['/module-subscriptions']
};

export const SubscriptionProvider = ({ children }) => {
  const [enabledModules, setEnabledModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  const fetchEnabledModules = useCallback(async () => {
    if (!token || !user) {
      setEnabledModules([]);
      setLoading(false);
      return;
    }

    try {
      const response = await api.getEnabledModules();
      setEnabledModules(response.data.enabled_modules || []);
    } catch (error) {
      console.error('Failed to fetch enabled modules:', error);
      // On error, don't restrict anything - show all modules
      setEnabledModules([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchEnabledModules();
  }, [fetchEnabledModules]);

  // Check if a specific route/path is enabled
  const isRouteEnabled = useCallback((path) => {
    // If still loading, don't filter (will be re-evaluated once loaded)
    if (loading) return true;
    
    // If no modules are configured (API returned empty or failed), show all
    if (enabledModules.length === 0) return true;
    
    // Always allow dashboard, core routes, and module-subscriptions (super_admin only page)
    const alwaysAllowed = ['/dashboard', '/', '/module-subscriptions'];
    if (alwaysAllowed.includes(path)) return true;

    // Find the module that controls this route
    for (const [moduleKey, routes] of Object.entries(MODULE_TO_ROUTES)) {
      if (routes.some(route => path.startsWith(route))) {
        return enabledModules.includes(moduleKey);
      }
    }
    
    // If route is not mapped to any module, allow it by default
    return true;
  }, [enabledModules, loading]);

  // Check if a specific module key is enabled
  const isModuleEnabled = useCallback((moduleKey) => {
    // If still loading, don't filter (will be re-evaluated once loaded)
    if (loading) return true;
    
    // If no modules are configured (API returned empty or failed), show all
    if (enabledModules.length === 0) return true;
    return enabledModules.includes(moduleKey);
  }, [enabledModules, loading]);

  // Refresh the modules (useful after super_admin updates config)
  const refreshModules = useCallback(() => {
    setLoading(true);
    fetchEnabledModules();
  }, [fetchEnabledModules]);

  return (
    <SubscriptionContext.Provider value={{ 
      enabledModules, 
      loading, 
      isRouteEnabled, 
      isModuleEnabled,
      refreshModules 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
